
import React, { useMemo } from 'react';
import { ChartBarIcon } from '../icons/InterfaceIcons';

// Fix: Define an interface for completed test objects to resolve type errors.
interface CompletedTest {
    test_id: number | string;
    attempted_at: string;
    score: number;
}

const parseAttemptDate = (dateString) => {
    const parts = dateString.split(' ');
    if (parts.length < 2) return new Date(0);
    const dateParts = parts[0].split('/');
    const timeParts = parts[1].split(':');
    
    if (dateParts.length !== 3 || timeParts.length !== 3) return new Date(0);

    const day = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1;
    const year = parseInt(dateParts[2], 10);
    
    let hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    const seconds = parseInt(timeParts[2], 10);

    if (parts[2] && parts[2].toLowerCase() === 'pm' && hours < 12) {
        hours += 12;
    }
    if (parts[2] && parts[2].toLowerCase() === 'am' && hours === 12) {
        hours = 0;
    }
    
    return new Date(year, month, day, hours, minutes, seconds);
};

export const RecentPerformanceCard = ({ completedTests }) => {
    const recentPerformance = useMemo(() => {
        if (!completedTests) return [];
        // Fix: Cast the array of tests to the correct type to allow sorting and property access.
        const sortedTests = (Object.values(completedTests) as CompletedTest[])
            .sort((a, b) => parseAttemptDate(b.attempted_at).getTime() - parseAttemptDate(a.attempted_at).getTime());
        
        const last5Tests = sortedTests.slice(-5);
        
        return last5Tests.map((test, index) => ({
            id: test.test_id.toString() + index,
            label: `T-${last5Tests.length - index - 1}`,
            score: test.score,
        }));

    }, [completedTests]);
    
    const maxScore = recentPerformance.length > 0 ? Math.max(...recentPerformance.map(p => p.score), 100) : 100;

    return (
        <div className="bg-brand-surface border border-brand-border rounded-xl p-6 h-full shadow-card">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-xl text-brand-text-primary">Recent Performance</h3>
                <ChartBarIcon className="w-6 h-6 text-brand-text-secondary"/>
            </div>
            {recentPerformance.length > 0 ? (
                <>
                    <div className="flex items-end justify-around h-32">
                        {recentPerformance.map(record => (
                            <div key={record.id} className="flex flex-col items-center w-1/5 text-center">
                                <div 
                                    className="w-3/5 bg-brand-primary/20 rounded-t-md hover:bg-brand-primary/40 transition-colors"
                                    style={{ height: `${(record.score / maxScore) * 100}%` }}
                                    title={`Score: ${record.score}`}
                                />
                                <span className="text-xs text-brand-text-secondary mt-2">{record.label}</span>
                            </div>
                        ))}
                    </div>
                     <p className="text-sm text-brand-text-secondary mt-4 text-center">
                        Your performance in the last {recentPerformance.length} test{recentPerformance.length > 1 ? 's' : ''}.
                    </p>
                </>
            ) : (
                <div className="flex items-center justify-center h-40">
                    <p className="text-center text-sm text-brand-text-secondary">Complete some tests to see your performance analysis here.</p>
                </div>
            )}
        </div>
    );
};
